import { Injectable } from '@angular/core';
import { DateService } from '../../date/date.service';
import {
  DailyLogOperationInstance,
  DataForDay,
  DailyLogType,
  DailyOperationLogRecordHelper,
  DailyOperationLogRecord,
  Step,
  StepInstance,
  StepSelectionMethod
} from '../../../models/projects/operation.models';
import { DailyLogService } from '../daily-log.service';
import { OperationService } from '../../operations/operation/operation.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DateParserService } from '../daily-log-time/date-parser-service';
import { cloneDeep } from 'lodash';
import { LocationChangeEditor, SetupChangeLogRecord } from '../../../models/setup/setup.models';
import * as moment from 'moment';
import { LockedDayService } from '../../locked-day/locked-day.service';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { AppDataSummary } from '../../../models/app-data/app-data';
import { DateTime } from 'luxon';

@Injectable()
export class DailyLogEntriesService {

  constructor(
    private dateService: DateService,
    private dailyLogService: DailyLogService,
    private operationService: OperationService,
    private dateParserService: DateParserService,
    private lockedDayService: LockedDayService,
    private timeZoneService: TimeZoneService,
  ) {

  }

  async createOperationStartLogEntry(
    id: number,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
    secondarySortTime?: PtLuxonDate,
    localOperationInstance?: DailyLogOperationInstance,
    datesAmbiguous?: boolean
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    const oldLog = this.dailyLogService.getOperationStartTimeLog(oldLogs);
    let chosenId = this.chooseId(id, oldLog);
    let localId = null;

    if (chosenId < 1) {
      localId = chosenId;
      chosenId = null;
    }

    operationInstance.startTime = PtLuxonDate.assignPtLuxonDate(operationInstance.startTime);

    if (!!operationInstance.startTime.getDate()) {
      const startRecord: DailyOperationLogRecord = {
        id: <number>chosenId,
        projectSpecificOperationApiId: null,
        setupChangeLogApiId: null,
        localId: <number>localId,
        guid: '',
        isContractual: !this.operationService
          .isTypeAtTimeConcurrent(operationInstance, operationInstance.startTime.getDate()),
        contractualConcurrentText: '',
        operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
        isInterruption: operationInstance.isInterruption,
        lineTypeName: 'Operation Start',
        startTime: cloneDeep(operationInstance.startTime),
        startTimeShow: localOperationInstance
          ? localOperationInstance.startTimeToShow
          : this.dateService.setTimeStringFromDateTime(operationInstance.startTime.dateTime),
        endTime: null,
        endTimeShow: this.dateService.setTimeStringFromDateTime(null),
        startTimeOffset: localOperationInstance
          ? localOperationInstance.startTime.dateTime.offset / 60
          : await this.timeZoneService.offsetForTime(operationInstance.startTime.dateTime, null, datesAmbiguous),
        endTimeOffset: null,
        comment: operationInstance.startComment,
        commentAppearOnDPR: operationInstance.startComment != null
          ? operationInstance.startComment.appear
          : null,
        operationDefinition: operationInstance.projectSpecificOperation.operationSummary.startDefinition,
        stepInstance: null,
        operationName: operationInstance.projectSpecificOperation.name
          ? operationInstance.projectSpecificOperation.name
          : operationInstance.projectSpecificOperation.operationSummary.name,
        operationInstanceId: operationInstance.id,
        operationInstanceLocalId: operationInstance.localId,
        stageIds: operationInstance.projectSpecificOperation.stageIds,
        sortTime: cloneDeep(operationInstance.startTime.getDate()),
        typeId: DailyLogType.OperationStart,
        selected: false,
        selectedSwap: false,
        selectedSwapHighlight: false,
        disabled: false,
        startTimeConfirmed: operationInstance.startConfirmed,
        endTimeConfirmed: false,
        ordinal: oldLog
          ? oldLog.ordinal
          : operationInstance.isConcurrentOperation
            ? 10 : 9,
        uniqueOrdinal: uniqueOrdinal,
        stepNumber: null,
        commentIdentifier: null,
        secondaryGuid: null,
        operationStartTime: !secondarySortTime ? cloneDeep(operationInstance.startTime) : cloneDeep(secondarySortTime),
        operationEndTime: cloneDeep(operationInstance.endTime),
        archived: operationInstance.startTime.dateTime == null,
        canConfirmTimes: true,
        customSort: null,
      };

      if (startRecord.operationClassification === 'Delay') {
        startRecord.ordinal = 14;
      }

      if (!!oldLog) {
        startRecord.uniqueOrdinal = oldLog.uniqueOrdinal;
      }

      // Seed point milestones when records are generated, preserving any existing manual ordering.
      if ((!startRecord.isContractual || startRecord.isInterruption)
        && operationInstance.startTime.dateTime != null && operationInstance.endTime != null && operationInstance.endTime.dateTime != null
        && operationInstance.startTime.dateTime.valueOf() === operationInstance.endTime.dateTime.valueOf()) {
        startRecord.customSort = oldLog && oldLog.customSort != null ? oldLog.customSort : startRecord.uniqueOrdinal;
      }

      id--;
      uniqueOrdinal++;

      helper.records.push(startRecord);
      helper.nextId = id;
      helper.nextUniqueOrdinal = uniqueOrdinal;
    }

    return helper;
  }

  // loops through the steps in an operation and sets sort times to the previous steps' sort time if it's null
  setStepNullSortTimes(dailyLogOperationInstance: DailyLogOperationInstance): DailyLogOperationInstance {

    const steps = dailyLogOperationInstance.stepCollectionInstance != null ?
      dailyLogOperationInstance.stepCollectionInstance.stepInstances : null;

    if (steps == null || steps.length === 0) {
      return dailyLogOperationInstance;
    }

    if (dailyLogOperationInstance.stepCollectionInstance.stepInstances[0].sortTime == null) {
      dailyLogOperationInstance.stepCollectionInstance.stepInstances[0].sortTime
        = dailyLogOperationInstance.startTime.dateTime;
    }

    for (let i = 1; i < dailyLogOperationInstance.stepCollectionInstance.stepInstances.length; i++) {
      if (dailyLogOperationInstance.stepCollectionInstance.stepInstances[i].sortTime == null) {
        dailyLogOperationInstance.stepCollectionInstance.stepInstances[i].sortTime =
          dailyLogOperationInstance.stepCollectionInstance.stepInstances[i - 1].startTime.dateTime;
      }
    }
    return dailyLogOperationInstance;
  }

  async createOperationStepLogEntry(
    id: number,
    stepInstances: StepInstance[],
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
    localOperationInstance?: DailyLogOperationInstance,
    datesAmbiguous?: boolean,
    useStepNumberComparison?: boolean
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    const stepRecords: DailyOperationLogRecord[] = [];

    operationInstance = this.setStepNullSortTimes(operationInstance);
    operationInstance = await this.operationService.deserializeInstanceTimes(operationInstance);

    for (let i = 0; i < stepInstances.length; i++) {
      const element = stepInstances[i];
      const oldLog = this.dailyLogService.getOperationStepLog(oldLogs,
        element.id && !useStepNumberComparison
          ? element.id
          : element.number,
        element.id && !useStepNumberComparison
          ? StepSelectionMethod.id
          : StepSelectionMethod.stepNumber,
        element.group);
      let chosenId = this.chooseId(id, oldLog);
      let localId = null;

      if (chosenId < 1) {
        localId = chosenId;
        chosenId = null;
      }

      if (!element.startTimeToShow && oldLog) {
        oldLog.archived = true;
        oldLog.startTime = element.startTime;
        oldLog.startTimeShow = element.startTimeToShow;
        oldLog.endTime = element.endTime;
        oldLog.endTimeShow = element.endTimeToShow;

        await this.dailyLogService.saveDailyLogRecord(chosenId, oldLog);
        stepRecords.push(oldLog);
        id--;
        continue;
      }

      if (!element.dailyLogRecordId) {
        element.dailyLogRecordId = <number>chosenId;
      }

      if (!element.dailyLogRecordLocalId) {
        element.dailyLogRecordLocalId = id;
      }

      const record: DailyOperationLogRecord = {
        id: <number>chosenId,
        projectSpecificOperationApiId: null,
        setupChangeLogApiId: null,
        localId: localId,
        guid: '',
        isContractual: !this.operationService.isTypeAtTimeConcurrent(operationInstance, element.startTime.getDate()),
        contractualConcurrentText: '',
        operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
        isInterruption: operationInstance.isInterruption,
        lineTypeName: 'Step',
        startTime: cloneDeep(element.startTime),
        startTimeShow: localOperationInstance
          ? localOperationInstance.stepCollectionInstance.stepInstances[i].startTimeToShow
          : this.dateService.setTimeStringFromDateTime(element.startTime.dateTime),
        endTime: cloneDeep(element.endTime),
        endTimeShow: localOperationInstance
          ? localOperationInstance.stepCollectionInstance.stepInstances[i].endTimeToShow
          : this.dateService.setTimeStringFromDateTime(element.endTime.dateTime),
        startTimeOffset: localOperationInstance && localOperationInstance.stepCollectionInstance.stepInstances[i].startTime.dateTime
          ? localOperationInstance.stepCollectionInstance.stepInstances[i].startTime.dateTime.offset / 60
          : await this.timeZoneService.offsetForTime(element.startTime.dateTime, null, datesAmbiguous),
        endTimeOffset: localOperationInstance
          ? localOperationInstance.stepCollectionInstance.stepInstances[i].endTime && localOperationInstance.stepCollectionInstance.stepInstances[i].endTime.dateTime
            ? localOperationInstance.stepCollectionInstance.stepInstances[i].endTime.dateTime.offset / 60
            : null
          : await this.timeZoneService.offsetForTime(element.endTime.dateTime, null, datesAmbiguous),
        comment: element.comment,
        commentAppearOnDPR: element.comment != null ? element.comment.appear : null,
        stepInstance: element,
        operationName: element.step.name,
        operationInstanceId: operationInstance.id,
        operationInstanceLocalId: operationInstance.localId,
        stageIds: operationInstance.projectSpecificOperation.stageIds,
        sortTime: element.startTime.dateTime ? cloneDeep(element.startTime.getDate()) : cloneDeep(element.sortTime),
        typeId: DailyLogType.Step,
        selected: false,
        selectedSwap: false,
        selectedSwapHighlight: false,
        disabled: false,
        startTimeConfirmed: element.startConfirmed,
        endTimeConfirmed: element.endConfirmed,
        ordinal: oldLog
          ? oldLog.ordinal
          : operationInstance.isConcurrentOperation ? 19 : 18,
        uniqueOrdinal: uniqueOrdinal,
        stepNumber: element.number,
        commentIdentifier: null,
        secondaryGuid: null,
        operationStartTime: cloneDeep(operationInstance.startTime),
        operationEndTime: cloneDeep(operationInstance.endTime),
        archived: element.exclude || element.startTime.dateTime == null
          || (!!operationInstance.cancelledTime
            && !isNaN(operationInstance.cancelledTime.toMillis())
            && (operationInstance.cancelledTime < element.endTime.dateTime || !element.endTime.getDate())),
        canConfirmTimes: true,
        customSort: null
      };

      if (record.operationClassification === 'Delay') {
        record.ordinal = 15;
      }

      if (!!oldLog) {
        record.uniqueOrdinal = oldLog.uniqueOrdinal;
      }

      id--;
      uniqueOrdinal++;
      stepRecords.push(record);
    }

    helper.records = stepRecords;
    helper.nextId = id;
    helper.nextUniqueOrdinal = uniqueOrdinal;

    return helper;
  }

  async createOperationEndLogEntry(
    id: number,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
    secondarySortTime?: PtLuxonDate,
    localOperationInstance?: DailyLogOperationInstance,
    datesAmbiguous?: boolean
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    const oldLog = this.dailyLogService.getOperationEndTimeLog(oldLogs);
    let chosenId = this.chooseId(id, oldLog);
    let localId = null;

    if (chosenId < 1) {
      localId = chosenId;
      chosenId = null;
    }

    operationInstance = this.setStepNullSortTimes(operationInstance);
    const mostRecentEndTime = operationInstance.endTime.dateTime;

    const endRecord: DailyOperationLogRecord = {
      id: <number>chosenId,
      projectSpecificOperationApiId: null,
      setupChangeLogApiId: null,
      localId: <number>localId,
      guid: '',
      isContractual: !this.operationService.isTypeAtTimeConcurrent(operationInstance,
        operationInstance.endTime.getDate()),
      contractualConcurrentText: '',
      operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
      isInterruption: operationInstance.isInterruption,
      lineTypeName: 'Operation End',
      startTime: null,
      startTimeShow: null,
      endTime: cloneDeep(operationInstance.endTime),
      endTimeShow: localOperationInstance
        ? localOperationInstance.endTimeToShow
        : this.dateService.setTimeStringFromDateTime(operationInstance.endTime.dateTime),
      startTimeOffset: localOperationInstance
        ? localOperationInstance.endTime && localOperationInstance.endTime.dateTime
          ? localOperationInstance.endTime.dateTime.offset / 60
          : null
        : await this.timeZoneService.offsetForTime(operationInstance.endTime.dateTime, null, datesAmbiguous),
      endTimeOffset: localOperationInstance
        ? localOperationInstance.endTime && localOperationInstance.endTime.dateTime
          ? localOperationInstance.endTime.dateTime.offset / 60
          : null
        : await this.timeZoneService.offsetForTime(operationInstance.endTime.dateTime, null, datesAmbiguous),
      comment: operationInstance.endComment,
      commentAppearOnDPR: operationInstance.endComment != null ? operationInstance.endComment.appear : null,
      operationDefinition: operationInstance.projectSpecificOperation.operationSummary.endDefinition,
      stepInstance: null,
      operationName: operationInstance.projectSpecificOperation.name
        ? operationInstance.projectSpecificOperation.name
        : operationInstance.projectSpecificOperation.operationSummary.name,
      operationInstanceId: operationInstance.id,
      operationInstanceLocalId: operationInstance.localId,
      stageIds: operationInstance.projectSpecificOperation.stageIds,
      sortTime: operationInstance.endTime
        ? cloneDeep(operationInstance.endTime.getDate())
        : cloneDeep(mostRecentEndTime),
      typeId: DailyLogType.OperationEnd,
      selected: false,
      selectedSwap: false,
      selectedSwapHighlight: false,
      disabled: false,
      startTimeConfirmed: false,
      endTimeConfirmed: operationInstance.endConfirmed,
      ordinal: oldLog
        ? oldLog.ordinal
        : operationInstance.isConcurrentOperation ? 22 : 21,
      uniqueOrdinal: uniqueOrdinal,
      stepNumber: null,
      commentIdentifier: null,
      secondaryGuid: null,
      operationStartTime: !secondarySortTime ? cloneDeep(operationInstance.startTime) : cloneDeep(secondarySortTime),
      operationEndTime: cloneDeep(operationInstance.endTime),
      archived: !operationInstance.endTimeToShow
        || (!!operationInstance.cancelledTime
          && !isNaN(operationInstance.cancelledTime.toMillis())
          && operationInstance.cancelledTime < operationInstance.endTime.getDate()),
      canConfirmTimes: true,
      customSort: null
    };

    if (endRecord.operationClassification === 'Delay') {
      endRecord.ordinal = 16;
    }

    if (!!oldLog) {
      endRecord.uniqueOrdinal = oldLog.uniqueOrdinal;
    }

    // Seed point milestones when records are generated, preserving any existing manual ordering.
    if ((!endRecord.isContractual || endRecord.isInterruption)
      && operationInstance.startTime.dateTime != null && operationInstance.endTime != null && operationInstance.endTime.dateTime != null
      && operationInstance.startTime.dateTime.valueOf() === operationInstance.endTime.dateTime.valueOf()) {
      endRecord.customSort = oldLog && oldLog.customSort != null ? oldLog.customSort : endRecord.uniqueOrdinal;
    }

    id--;
    uniqueOrdinal++;
    helper.records.push(endRecord);
    helper.nextId = id;
    helper.nextUniqueOrdinal = uniqueOrdinal;

    return helper;
  }

  async createOperationCommentLogEntry(
    id: number,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];

    for (const element of operationInstance.comments) {
      if (!element.startTimeToShow && !element.endTimeToShow) {
        return;
      }

      let oldLog = this.dailyLogService.getOperationCommentLog(oldLogs, null, element.recordApiId);
      oldLog = await this.dateParserService.deserializeDates(oldLog);
      let chosenId = this.chooseId(id, oldLog);
      let localId = null;

      if (chosenId < 1) {
        localId = chosenId;
        chosenId = null;
      }

      let sortTime = null;

      // let endTime = null;
      if (element.endTimeDate && element.endTimeDate.dateTime) {
        sortTime = element.endTimeDate.dateTime;
      }

      // let startTime = null;
      if (element.startTimeDate && element.startTimeDate.dateTime) {
        sortTime = element.startTimeDate.dateTime;
      }

      const record: DailyOperationLogRecord = {
        id: <number>chosenId,
        projectSpecificOperationApiId: null,
        setupChangeLogApiId: null,
        localId: <number>localId,
        guid: '',
        isContractual: false,
        contractualConcurrentText: '',
        operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
        isInterruption: false,
        lineTypeName: 'Comment',
        startTime: (element.startTimeDate),
        startTimeShow: element.startTimeToShow,
        endTime: cloneDeep(element.endTimeDate),
        endTimeShow: element.endTimeToShow,
        startTimeOffset: (!element.startTimeDate || (element.startTimeDate && !element.startTimeDate.dateTime))
          ? 0
          : element.startTimeDate.dateTime.offset / 60,
        endTimeOffset: (!element.endTimeDate || element.endTimeDate && !element.endTimeDate.dateTime)
          ? 0
          : element.endTimeDate.dateTime.offset / 60,
        comment: element,
        commentAppearOnDPR: element.appear,
        stepInstance: null,
        operationName: operationInstance.projectSpecificOperation.name
          ? operationInstance.projectSpecificOperation.name
          : operationInstance.projectSpecificOperation.operationSummary.name,
        operationInstanceId: operationInstance.id,
        operationInstanceLocalId: operationInstance.localId,
        stageIds: operationInstance.projectSpecificOperation.stageIds,
        sortTime: cloneDeep(sortTime),
        typeId: DailyLogType.Comment,
        selected: false,
        selectedSwap: false,
        selectedSwapHighlight: false,
        disabled: false,
        startTimeConfirmed: false,
        endTimeConfirmed: false,
        ordinal: oldLog
          ? oldLog.ordinal
          : 20,
        uniqueOrdinal: uniqueOrdinal,
        stepNumber: null,
        commentIdentifier: element.identifier,
        operationStartTime: cloneDeep(operationInstance.startTime),
        operationEndTime: cloneDeep(operationInstance.endTime),
        archived: false,
        secondaryGuid: null,
        canConfirmTimes: true,
        customSort: null
      };

      await this.dateParserService.deserializeDates(record);

      if (!!oldLog) {
        record.uniqueOrdinal = oldLog.uniqueOrdinal;
      }

      id--;
      uniqueOrdinal++;
      helper.records.push(record);
      helper.nextId = id;
      helper.nextUniqueOrdinal = uniqueOrdinal;
    }

    return helper;
  }

  async createTransitingToLogEntry(
    locationName: string,
    id: number,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
    transitingOrdinal?: number,
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    const oldLog = this.dailyLogService.getOperationTransitingToLog(oldLogs, id);
    let chosenId = this.chooseId(id, oldLog);
    let localId = null;

    if (chosenId < 1) {
      localId = chosenId;
      chosenId = null;
    }

    if (!!operationInstance.startTime.getDate()) {
      const transitingRecord: DailyOperationLogRecord = {
        id: <number>chosenId,
        projectSpecificOperationApiId: null,
        setupChangeLogApiId: oldLog && oldLog.setupChangeLogApiId ? oldLog.setupChangeLogApiId : null,
        localSetupChangeLogRecordId: oldLog && oldLog.localSetupChangeLogRecordId ? oldLog.localSetupChangeLogRecordId : null,
        localId: <number>localId,
        guid: '',
        isContractual: !this.operationService.isTypeAtTimeConcurrent(operationInstance, operationInstance.startTime.getDate()),
        contractualConcurrentText: '',
        operationClassification: '',
        isInterruption: false,
        lineTypeName: 'Transiting To',
        startTime: cloneDeep(operationInstance.startTime),
        endTime: null,
        comment: null,
        stepInstance: null,
        operationName: locationName,
        startTimeShow: operationInstance.startTimeToShow,
        endTimeShow: null,
        startTimeOffset: operationInstance.startTime.dateTime.offset / 60,
        endTimeOffset: null,
        commentAppearOnDPR: null,
        operationInstanceId: operationInstance.id,
        operationInstanceLocalId: operationInstance.localId,
        stageIds: null,
        sortTime: cloneDeep(operationInstance.startTime.getDate()),
        typeId: DailyLogType.Transiting,
        selected: false,
        selectedSwap: false,
        selectedSwapHighlight: false,
        disabled: false,
        startTimeConfirmed: false,
        endTimeConfirmed: false,
        ordinal: oldLog
          ? oldLog.ordinal
          : 8,
        uniqueOrdinal: transitingOrdinal ? transitingOrdinal : uniqueOrdinal,
        stepNumber: null,
        commentIdentifier: null,
        operationStartTime: cloneDeep(operationInstance.startTime),
        operationEndTime: cloneDeep(operationInstance.endTime),
        archived: false,
        secondaryGuid: null,
        canConfirmTimes: true,
        customSort: null
      };

      if (!!oldLog) {
        transitingRecord.uniqueOrdinal = oldLog.uniqueOrdinal;
      }

      id--;
      uniqueOrdinal++;
      helper.records.push(transitingRecord);
      helper.nextId = id;
      helper.nextUniqueOrdinal = uniqueOrdinal;
    }

    return helper;
  }

  // used specifically for when a location changes as a result of a transit operation being recorded
  async createLocationChangeLogEntry(
    editor: LocationChangeEditor,
    locationSetupId: number,
    datesAmbiguous?: boolean
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];

    let operationName = 'To ' + editor.newName;

    if (editor.previousName !== null && editor.previousName !== undefined) {
      operationName = 'From ' + editor.previousName + ' ' + operationName;
    }

    const record: DailyOperationLogRecord = {
      id: null,
      projectSpecificOperationApiId: null,
      setupChangeLogApiId: null,
      localId: <number>editor.id,
      guid: '',
      isContractual: true,
      contractualConcurrentText: '',
      operationClassification: '',
      isInterruption: false,
      lineTypeName: editor.classification + ' Change',
      startTime: cloneDeep(editor.operationInstance.endTime),
      endTime: null,
      comment: null,
      stepInstance: null,
      operationName: operationName,
      startTimeShow: editor.operationInstance.endTimeToShow,
      endTimeShow: null,
      // note that startTimeOffset is set from operation END time - this is correct
      startTimeOffset: editor.operationInstance.endTime && editor.operationInstance.endTime.dateTime ? editor.operationInstance.endTime.dateTime.offset / 60 : null,
      endTimeOffset: null,
      commentAppearOnDPR: null,
      operationInstanceId: editor.operationInstance.id,
      operationInstanceLocalId: editor.operationInstance.localId,
      stageIds: null,
      sortTime: cloneDeep(editor.operationInstance.endTime.getDate()),
      typeId: editor.type,
      localSetupChangeLogRecordId: locationSetupId,
      selected: false,
      selectedSwap: false,
      selectedSwapHighlight: false,
      disabled: false,
      startTimeConfirmed: false,
      endTimeConfirmed: false,
      ordinal: 23,
      uniqueOrdinal: editor.uniqueOrdinal,
      stepNumber: null,
      commentIdentifier: null,
      operationStartTime: cloneDeep(editor.operationInstance.startTime),
      operationEndTime: cloneDeep(editor.operationInstance.endTime),
      archived: false,
      secondaryGuid: null,
      canConfirmTimes: true,
      customSort: null
    };

    helper.records.push(record);

    return helper;
  }

  async createCancelEntries(
    id: number,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
    datesAmbiguous?: boolean
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    const oldLog = this.dailyLogService.getOperationCancelLog(oldLogs);

    let chosenId = this.chooseId(id, oldLog);
    let localId = null;

    if (chosenId < 1) {
      localId = chosenId;
      chosenId = null;
    }

    const cancelledPtDate = new PtLuxonDate(operationInstance.cancelledTime);

    if (!!operationInstance.startTime.getDate()) {
      const cancelRecord: DailyOperationLogRecord = {
        id: <number>chosenId,
        projectSpecificOperationApiId: null,
        setupChangeLogApiId: null,
        localId: <number>localId,
        guid: '',
        isContractual: !this.operationService.isTypeAtTimeConcurrent(operationInstance, operationInstance.cancelledTime),
        contractualConcurrentText: '',
        operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
        isInterruption: false,
        lineTypeName: 'Cancelled',
        startTime: new PtLuxonDate(operationInstance.cancelledTime),
        endTime: null,
        comment: null,
        stepInstance: null,
        operationName: operationInstance.projectSpecificOperation.name
          ? operationInstance.projectSpecificOperation.name
          : operationInstance.projectSpecificOperation.operationSummary.name,
        startTimeShow: this.dateService.setTimeStringFromPtLuxonDate(cancelledPtDate),
        endTimeShow: null,
        startTimeOffset: await this.timeZoneService.offsetForTime(operationInstance.cancelledTime, null, datesAmbiguous),
        endTimeOffset: null,
        commentAppearOnDPR: null,
        operationInstanceId: operationInstance.id,
        operationInstanceLocalId: operationInstance.localId,
        stageIds: null,
        sortTime: cloneDeep(operationInstance.cancelledTime),
        typeId: DailyLogType.Cancel,
        selected: false,
        selectedSwap: false,
        selectedSwapHighlight: false,
        disabled: false,
        startTimeConfirmed: false,
        endTimeConfirmed: false,
        ordinal: oldLog
          ? oldLog.ordinal
          : 7,
        uniqueOrdinal: uniqueOrdinal,
        stepNumber: null,
        commentIdentifier: null,
        operationStartTime: cloneDeep(operationInstance.startTime),
        operationEndTime: cloneDeep(operationInstance.endTime),
        archived: false,
        secondaryGuid: null,
        canConfirmTimes: true,
        customSort: null
      };

      if (!!oldLog) {
        cancelRecord.uniqueOrdinal = oldLog.uniqueOrdinal;
      }

      id--;
      uniqueOrdinal++;
      helper.records.push(cancelRecord);
      helper.nextId = id;
      helper.nextUniqueOrdinal = uniqueOrdinal;
    }

    return helper;
  }

  async createSwitchTimeEntries(
    id: number,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
    datesAmbiguous?: boolean
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];

    for (const element of operationInstance.switchTimes) {
      let oldLog = this.dailyLogService.getOperationSwitchLogStart(oldLogs, element.guid);
      let chosenId = this.chooseId(id, oldLog);
      let localId = null;

      if (chosenId < 1) {
        localId = chosenId;
        chosenId = null;
      }

      if (!element) {
        continue;
      }

      if (element.date.dateTime < operationInstance.startTime.dateTime
        || (element.date.dateTime > operationInstance.endTime.dateTime
          && !!operationInstance.endTime.getDate())) {
        continue;
      }

      if (!!operationInstance.startTime.getDate()) {
        const switchRecord: DailyOperationLogRecord = {
          id: null,
          projectSpecificOperationApiId: null,
          setupChangeLogApiId: null,
          localId: <number>localId,
          guid: '',
          isContractual: !element.isConcurrent,
          contractualConcurrentText: '',
          operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
          isInterruption: false,
          lineTypeName: element.isConcurrent ? 'Concurrent Start' : 'Contractual Start',
          startTime: cloneDeep(element.date),
          endTime: null,
          comment: null,
          stepInstance: null,
          operationName: operationInstance.projectSpecificOperation.name
            ? operationInstance.projectSpecificOperation.name
            : operationInstance.projectSpecificOperation.operationSummary.name,
          startTimeShow: element.dateToShow,
          endTimeShow: null,
          startTimeOffset: element.dateOffset,
          endTimeOffset: null,
          commentAppearOnDPR: null,
          operationInstanceId: operationInstance.id,
          operationInstanceLocalId: operationInstance.localId,
          stageIds: null,
          sortTime: cloneDeep(element.date.dateTime),
          typeId: DailyLogType.TypeChangeStart,
          selected: false,
          selectedSwap: false,
          selectedSwapHighlight: false,
          disabled: false,
          startTimeConfirmed: false,
          endTimeConfirmed: false,
          ordinal: oldLog
            ? oldLog.ordinal
            : 12,
          uniqueOrdinal: uniqueOrdinal,
          stepNumber: null,
          commentIdentifier: null,
          operationStartTime: cloneDeep(operationInstance.startTime),
          operationEndTime: cloneDeep(operationInstance.endTime),
          archived: false,
          secondaryGuid: element.guid,
          canConfirmTimes: true,
          customSort: null
        };

        if (!!oldLog) {
          switchRecord.uniqueOrdinal = oldLog.uniqueOrdinal;
          switchRecord.id = oldLog.id;
        }

        id--;
        uniqueOrdinal++;
        helper.records.push(switchRecord);
        helper.nextId = id;
        helper.nextUniqueOrdinal = uniqueOrdinal;
      }

      oldLog = this.dailyLogService.getOperationSwitchLogEnd(oldLogs, element.guid);
      chosenId = this.chooseId(id, oldLog);

      if (chosenId < 1) {
        localId = chosenId;
        chosenId = null;
      }

      if (!element) {
        continue;
      }

      if (!!operationInstance.startTime.getDate()) {
        const switchRecord: DailyOperationLogRecord = {
          id: null,
          projectSpecificOperationApiId: null,
          setupChangeLogApiId: null,
          localId: <number>localId,
          guid: '',
          isContractual: !element.isConcurrent,
          contractualConcurrentText: '',
          operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
          isInterruption: false,
          lineTypeName: element.isConcurrent ? 'Contractual End' : 'Concurrent End',
          startTime: null,
          endTime: cloneDeep(element.date),
          comment: null,
          stepInstance: null,
          operationName: operationInstance.projectSpecificOperation.name
            ? operationInstance.projectSpecificOperation.name
            : operationInstance.projectSpecificOperation.operationSummary.name,
          startTimeShow: null,
          endTimeShow: element.dateToShow,
          startTimeOffset: element.dateOffset,
          endTimeOffset: element.dateOffset,
          commentAppearOnDPR: null,
          operationInstanceId: operationInstance.id,
          operationInstanceLocalId: operationInstance.localId,
          stageIds: null,
          sortTime: cloneDeep(element.date.dateTime),
          typeId: DailyLogType.TypeChangeEnd,
          selected: false,
          selectedSwap: false,
          selectedSwapHighlight: false,
          disabled: false,
          startTimeConfirmed: false,
          endTimeConfirmed: false,
          ordinal: oldLog
            ? oldLog.ordinal
            : 11,
          uniqueOrdinal: uniqueOrdinal,
          stepNumber: null,
          commentIdentifier: null,
          operationStartTime: cloneDeep(operationInstance.startTime),
          operationEndTime: cloneDeep(operationInstance.endTime),
          archived: false,
          secondaryGuid: element.guid,
          canConfirmTimes: true,
          customSort: null
        };

        if (!!oldLog) {
          switchRecord.uniqueOrdinal = oldLog.uniqueOrdinal;
          switchRecord.id = oldLog.id;
        }

        id--;
        uniqueOrdinal++;
        helper.records.push(switchRecord);
        helper.nextId = id;
        helper.nextUniqueOrdinal = uniqueOrdinal;
      }
    }

    return helper;
  }

  async createPauseTimeEntries(
    id: number,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
    datesAmbiguous?: boolean
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];

    for (const element of operationInstance.pausedTimes) {
      element.startDate = PtLuxonDate.assignPtLuxonDate(element.startDate);
      element.endDate = PtLuxonDate.assignPtLuxonDate(element.endDate);

      const oldLog = this.dailyLogService.getOperationPauseLog(oldLogs, element.guid);
      const chosenId = this.chooseId(id, oldLog);

      if (!element) {
        continue;
      }

      if (!!operationInstance.startTime.getDate()) {
        const pauseRecord: DailyOperationLogRecord = {
          id: chosenId,
          projectSpecificOperationApiId: null,
          setupChangeLogApiId: null,
          localId: <number>chosenId,
          guid: '',
          isContractual: !this.operationService.isTypeAtTimeConcurrent(operationInstance, element.startDate.getDate()),
          contractualConcurrentText: '',
          operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
          isInterruption: false,
          lineTypeName: 'Operation Paused',
          startTime: cloneDeep(element.startDate),
          endTime: null,
          comment: null,
          stepInstance: null,
          operationName: operationInstance.projectSpecificOperation.name
            ? operationInstance.projectSpecificOperation.name
            : operationInstance.projectSpecificOperation.operationSummary.name,
          startTimeShow: this.dateService.setTimeStringFromPtLuxonDate(element.startDate),
          endTimeShow: null,
          startTimeOffset: element.startDateOffset,
          endTimeOffset: null,
          commentAppearOnDPR: null,
          operationInstanceId: operationInstance.id,
          operationInstanceLocalId: operationInstance.localId,
          stageIds: null,
          sortTime: cloneDeep(element.startDate.getDate()),
          typeId: DailyLogType.Paused,
          selected: false,
          selectedSwap: false,
          selectedSwapHighlight: false,
          disabled: false,
          startTimeConfirmed: false,
          endTimeConfirmed: false,
          ordinal: oldLog
            ? oldLog.ordinal
            : 13,
          uniqueOrdinal: uniqueOrdinal,
          stepNumber: null,
          commentIdentifier: null,
          operationStartTime: cloneDeep(operationInstance.startTime),
          operationEndTime: cloneDeep(operationInstance.endTime),
          archived: false,
          secondaryGuid: element.guid,
          canConfirmTimes: true,
          customSort: null
        };

        if (!!oldLog) {
          pauseRecord.uniqueOrdinal = oldLog.uniqueOrdinal;
        }

        id--;
        uniqueOrdinal++;
        helper.records.push(pauseRecord);
        helper.nextId = id;
        helper.nextUniqueOrdinal = uniqueOrdinal;
      }
    }

    return helper;
  }

  async createResumedTimeEntries(
    id: number,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    oldLogs?: DailyOperationLogRecord[],
    datesAmbiguous?: boolean
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    for (const element of operationInstance.pausedTimes) {

      if (!element.endDate.dateTime || isNaN(element.endDate.dateTime.toMillis())) {
        continue;
      }

      const oldLog = this.dailyLogService.getOperationResumeLog(oldLogs, element.guid);
      const chosenId = this.chooseId(id, oldLog);

      if (!element) {
        continue;
      }

      if (!!operationInstance.startTime.getDate()) {
        const resumeRecord: DailyOperationLogRecord = {
          id: chosenId,
          projectSpecificOperationApiId: null,
          setupChangeLogApiId: null,
          localId: <number>chosenId,
          guid: '',
          isContractual: !this.operationService.isTypeAtTimeConcurrent(operationInstance, element.endDate.getDate()),
          contractualConcurrentText: '',
          operationClassification: operationInstance.projectSpecificOperation.operationSummary.classificationName,
          isInterruption: false,
          lineTypeName: 'Operation Resumed',
          startTime: cloneDeep(element.endDate),
          endTime: null,
          comment: null,
          stepInstance: null,
          operationName: operationInstance.projectSpecificOperation.name
            ? operationInstance.projectSpecificOperation.name
            : operationInstance.projectSpecificOperation.operationSummary.name,
          startTimeShow: this.dateService.setTimeStringFromPtLuxonDate(element.endDate),
          endTimeShow: null,
          startTimeOffset: element.endDateOffset,
          endTimeOffset: null,
          commentAppearOnDPR: null,
          operationInstanceId: operationInstance.id,
          operationInstanceLocalId: operationInstance.localId,
          stageIds: null,
          sortTime: element.endDate.dateTime,
          typeId: DailyLogType.Resumed,
          selected: false,
          selectedSwap: false,
          selectedSwapHighlight: false,
          disabled: false,
          startTimeConfirmed: false,
          endTimeConfirmed: false,
          ordinal: oldLog
            ? oldLog.ordinal
            : 17,
          uniqueOrdinal: uniqueOrdinal,
          stepNumber: null,
          commentIdentifier: null,
          operationStartTime: cloneDeep(operationInstance.startTime),
          operationEndTime: cloneDeep(operationInstance.endTime),
          archived: false,
          secondaryGuid: element.guid,
          canConfirmTimes: true,
          customSort: null
        };

        if (!!oldLog) {
          resumeRecord.uniqueOrdinal = oldLog.uniqueOrdinal;
        }

        id--;
        uniqueOrdinal++;
        helper.records.push(resumeRecord);
        helper.nextId = id;
        helper.nextUniqueOrdinal = uniqueOrdinal;
      }
    }

    return helper;
  }

  async createDateChangeRecord(
    id: number,
    endOfDay: PtLuxonDate,
    operationInstance?: DailyLogOperationInstance,
    uniqueOrdinal?: number
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];

    // using moment here as luxon can't use the 'Do' formatter, which displays the ordinal affix i.e 1'st', 3'rd'
    const newDateString: string = moment(this.dateService.convertDateTimeToLocalJSDate(endOfDay.dateTime))
       .format('Do MMMM');

    const ptStartTime = cloneDeep(endOfDay) as PtLuxonDate;
    ptStartTime.dateTime = ptStartTime.dateTime.minus({minute: 1});

    const record: DailyOperationLogRecord = {
      id: null,
      projectSpecificOperationApiId: null,
      setupChangeLogApiId: null,
      localId: <number>id,
      guid: '',
      isContractual: false,
      contractualConcurrentText: '',
      operationClassification: '',
      isInterruption: false,
      lineTypeName: 'Date Change',
      startTime: cloneDeep(ptStartTime),
      startTimeShow: '23:59',
      endTime: null,
      endTimeShow: null,
      startTimeOffset: await this.timeZoneService.offsetForTime(ptStartTime.dateTime, null, true),
      endTimeOffset: null,
      comment: null,
      commentAppearOnDPR: null,
      stepInstance: null,
      operationName: 'To ' + newDateString,
      operationInstanceId: null,
      stageIds: null,
      sortTime: cloneDeep(ptStartTime.dateTime),
      typeId: DailyLogType.DateChange,
      selected: false,
      selectedSwap: false,
      selectedSwapHighlight: false,
      disabled: false,
      startTimeConfirmed: false,
      endTimeConfirmed: false,
      ordinal: 1,
      uniqueOrdinal: uniqueOrdinal,
      stepNumber: null,
      commentIdentifier: null,
      operationStartTime: cloneDeep(ptStartTime),
      archived: false,
      secondaryGuid: null,
      canConfirmTimes: true,
      customSort: null
    };

    id--;
    uniqueOrdinal++;
    helper.records.push(record);
    helper.nextId = id;
    helper.nextUniqueOrdinal = uniqueOrdinal;

    return helper;
  }

  async getRecordsIncludingContinuingOperations(data: DataForDay): Promise<DailyOperationLogRecord[]> {
    let records = data.records;
    if (!data.continuingOperations?.length) {
      return records;
    }

    // Continued rows are generated in memory. Validation must include them even
    // when no start, step or end record was saved on this reporting day.
    const missingOperations = data.continuingOperations.filter(operation => !records.some(record =>
      record.typeId === DailyLogType.Continued && (operation.id
        ? record.operationInstanceId === operation.id
        : operation.localId && record.operationInstanceId === operation.localId)));

    if (missingOperations.length) {
      const helper = await this.createContinueRecords(0, data.startOfDay, missingOperations, 0);
      records = records.concat(helper.records);
    } else if (!data.previousDayData) {
      records = records.filter(record => record.typeId !== DailyLogType.Continued);
    }

    return records;
  }

  async createContinueRecords(
    id: number,
    startOfDay: PtLuxonDate,
    continuingOperations: DailyLogOperationInstance[],
    uniqueOrdinal: number
  ): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];

    const ptStartTime = cloneDeep(startOfDay) as PtLuxonDate;
    const startTimeOffset = startOfDay.dateTime.offset / 60;

    for (let i = 0; i < continuingOperations.length; i++) {
      const operation = continuingOperations[i];
      // check for steps that have the same group as another step
      // - this indicates that the step has been split into 2, which means it is continuing into next day and needs a continued record
      if (operation.stepCollectionInstance) {
        const step = operation.stepCollectionInstance.stepInstances
          .find(s1 => operation.stepCollectionInstance.stepInstances.some(s2 => s2.id !== s1.id && s2.group === s1.group));
        const stepGroup = step ? step.group : null;
        if (stepGroup) {
          const continuedSteps = operation.stepCollectionInstance.stepInstances
            .filter(e => e.group === stepGroup && e.sortTime)
            .sort(function (a, b) {
              return a.sortTime.toMillis() - b.sortTime.toMillis();
            });

          // also need to check that steps in that step group actually cross midnight, as they may have been split for another reason
          if (continuedSteps) {
            const sortTimes = continuedSteps.map(e => e.sortTime);
            let stepsDifferentDay = false;

            // check if any date exists in continuedSteps which differs from any other date, meaning the steps cross days
            for (let j = 0; j < sortTimes.length; j++) {
              for (let k = j + 1; k < sortTimes.length; k++) {
                if (await this.dateService.compareDayOffsetsPreApplied(sortTimes[k], sortTimes[j])) {
                  stepsDifferentDay = true;
                }
              }
            }

            if (stepsDifferentDay) {
              const stepRecord = this.createStepContinueRecord(id, continuedSteps[0], 0, operation, ptStartTime, startTimeOffset);
              id--;
              uniqueOrdinal++;
              helper.records.push(stepRecord);
            }
          }
        }
      }

      const record: DailyOperationLogRecord = {
        id: null,
        projectSpecificOperationApiId: null,
        setupChangeLogApiId: null,
        localId: <number>id,
        guid: '',
        isContractual: false,
        contractualConcurrentText: '',
        operationClassification: operation.projectSpecificOperation.operationSummary.classificationName,
        isInterruption: false,
        lineTypeName: 'Continued',
        startTime: cloneDeep(ptStartTime),
        startTimeShow: '00:00',
        endTime: null,
        endTimeShow: null,
        startTimeOffset: startTimeOffset,
        endTimeOffset: null,
        comment: null,
        commentAppearOnDPR: null,
        stepInstance: null,
        operationName: operation.projectSpecificOperation.name || operation.projectSpecificOperation.operationSummary.name,
        operationInstanceId: cloneDeep(operation.id),
        stageIds: null,
        sortTime: cloneDeep(ptStartTime.dateTime),
        typeId: DailyLogType.Continued,
        selected: false,
        selectedSwap: false,
        selectedSwapHighlight: false,
        disabled: false,
        startTimeConfirmed: false,
        endTimeConfirmed: false,
        ordinal: 0,
        uniqueOrdinal: uniqueOrdinal,
        stepNumber: null,
        commentIdentifier: null,
        operationStartTime: cloneDeep(ptStartTime),
        archived: false,
        secondaryGuid: null,
        canConfirmTimes: true,
        customSort: null
      };

      record.sortTime = this.dateService.setDateTimeSeconds(record.sortTime, 0);

      id--;
      uniqueOrdinal++;
      helper.records.push(record);
    }

    helper.nextId = id;
    helper.nextUniqueOrdinal = uniqueOrdinal;

    return helper;
  }

  createStepContinueRecord(
    id: number,
    continuingStep: StepInstance,
    uniqueOrdinal: number,
    parentOperation: DailyLogOperationInstance,
    ptStartTime: PtLuxonDate,
    startTimeOffset: number
  ): DailyOperationLogRecord {
    const copyOfStartTime = cloneDeep(ptStartTime);
    const copyOfPtStartTime = cloneDeep(copyOfStartTime);

    const record: DailyOperationLogRecord = {
      id: null,
      projectSpecificOperationApiId: null,
      setupChangeLogApiId: null,
      localId: <number>id,
      guid: '',
      isContractual: false,
      contractualConcurrentText: '',
      operationClassification: parentOperation.projectSpecificOperation.operationSummary.classificationName,
      isInterruption: false,
      lineTypeName: 'Continued Step',
      startTime: cloneDeep(ptStartTime),
      startTimeShow: '00:00',
      endTime: null,
      endTimeShow: null,
      startTimeOffset: startTimeOffset,
      endTimeOffset: null,
      comment: null,
      commentAppearOnDPR: null,
      stepInstance: null,
      operationName: continuingStep.step.name,
      operationInstanceId: cloneDeep(parentOperation.id),
      stageIds: null,
      sortTime: cloneDeep(ptStartTime.dateTime),
      typeId: DailyLogType.Continued,
      selected: false,
      selectedSwap: false,
      selectedSwapHighlight: false,
      disabled: false,
      startTimeConfirmed: false,
      endTimeConfirmed: false,
      ordinal: 1,
      uniqueOrdinal: uniqueOrdinal,
      stepNumber: null,
      commentIdentifier: null,
      operationStartTime: cloneDeep(copyOfPtStartTime),
      archived: false,
      secondaryGuid: null,
      canConfirmTimes: true,
      customSort: null
    };

    record.sortTime = this.dateService.setDateTimeSeconds(record.sortTime, 0);

    return record;
  }

  chooseId(id: number, oldLog?: DailyOperationLogRecord): number {
    if (!oldLog) {
      return id;
    }

    return oldLog.id ? oldLog.id : oldLog.localId;
  }

  async cleanseOperationStepLogEntries(instance: DailyLogOperationInstance): Promise<number[]> {
    const steps = !instance.stepCollectionInstance ? null : instance.stepCollectionInstance.stepInstances;

    if (!steps) {
      return;
    }

    const logs = await this.dailyLogService.getDailyLogDataForOperation(instance.id);
    const stepLogs = logs.filter(p => p.typeId === DailyLogType.Step);
    const stepNumbers = steps.map(p => p.number);
    const badLogs = stepLogs.filter(p => stepNumbers.indexOf(p.stepNumber) === -1);

    // for every locally saved log - if it doesn't exist in the supplied instance, delete it
    logs.forEach(log => {
      if (log.stepInstance && steps.findIndex(s => s.stepId === log.stepInstance.stepId && s.number === log.stepInstance.number) === -1) {
        badLogs.push(log);
      }
    });

    return badLogs.map(e => e.id);

    // used to actually delete the logs by calling the api, this has since been refactored to just return the Ids, leaving this in for now
    // for (let i = 0; i < badLogs.length; i++) {
    //   if (this.lockedDayService.getIsLockedDay()) {
    //     await this.dailyLogService.deleteLocalDailyLogRecord(badLogs[i].id);
    //   } else {
    //     await this.dailyLogService.deleteDailyLogRecord(badLogs[i].id);
    //   }
    // }
  }

  async removeCancelEntry(logs: DailyOperationLogRecord[]) {
    if (!logs) {
      return;
    }

    const cancelLogs = logs.filter(p => p.typeId === DailyLogType.Cancel);

    cancelLogs.forEach(log => {
      this.dailyLogService.deleteDailyLogRecord(log.id);
    });

  }

  async updateLocationChangeTime(id: number, cancelledTime: DateTime) {
    const logs = await this.dailyLogService.getDailyLogDataForOperation(id);
    const locationLog = logs.find(p => p.typeId === DailyLogType.LocationChange);

    if (!locationLog) {
      return;
    }

    if (!locationLog.startTime || !locationLog.startTime.dateTime) {
      locationLog.startTime = new PtLuxonDate(cloneDeep(cancelledTime));
    }

    locationLog.startTime.setDate(cancelledTime);
    locationLog.startTimeShow = this.dateService.setTimeStringFromDateTime(cancelledTime);
    locationLog.sortTime = cancelledTime;
    await this.dailyLogService.saveDailyLogData([locationLog]);
  }

  // takes a setup change log record, and finds and updates it's relevant daily log record, or creates a new daily log record if one doesn't exist
  async createOrUpdateTransitingToDailyLogEntry(
    transitingSetupChange: SetupChangeLogRecord,
    operationInstance: DailyLogOperationInstance,
    uniqueOrdinal: number,
    newLocalId: number): Promise<DailyOperationLogRecordHelper> {
    const helper = new DailyOperationLogRecordHelper();
    helper.records = [];
    let existingEntry = null;

    if (transitingSetupChange.dailyLogRecordId || transitingSetupChange.localDailyLogRecordId) {
      existingEntry = await this.dailyLogService.getDailyLogRecordDirect(transitingSetupChange.dailyLogRecordId || transitingSetupChange.localDailyLogRecordId);
    }

    if (!existingEntry) {
      const transitingRecord: DailyOperationLogRecord = {
        id: null,
        projectSpecificOperationApiId: null,
        setupChangeLogApiId: transitingSetupChange.id,
        localSetupChangeLogRecordId: transitingSetupChange.localId,
        localId: newLocalId,
        guid: '',
        isContractual: true,
        contractualConcurrentText: '',
        operationClassification: '',
        isInterruption: false,
        lineTypeName: 'Transiting To',
        startTime: cloneDeep(transitingSetupChange.timeOfChange),
        endTime: null,
        comment: null,
        stepInstance: null,
        operationName: transitingSetupChange.newValue,
        startTimeShow: transitingSetupChange.timeOfChangeToShow,
        endTimeShow: null,
        startTimeOffset: (transitingSetupChange.timeOfChange.dateTime.offset / 60),
        endTimeOffset: null,
        commentAppearOnDPR: null,
        operationInstanceId: operationInstance.id,
        operationInstanceLocalId: operationInstance.localId,
        stageIds: null,
        sortTime: cloneDeep(transitingSetupChange.timeOfChange.dateTime),
        typeId: DailyLogType.Transiting,
        selected: false,
        selectedSwap: false,
        selectedSwapHighlight: false,
        disabled: false,
        startTimeConfirmed: false,
        endTimeConfirmed: false,
        ordinal: 8,
        uniqueOrdinal: uniqueOrdinal,
        stepNumber: null,
        commentIdentifier: null,
        operationStartTime: cloneDeep(operationInstance.startTime),
        operationEndTime: cloneDeep(operationInstance.endTime),
        archived: false,
        secondaryGuid: null,
        canConfirmTimes: true,
        customSort: null
      };

      uniqueOrdinal++;
      newLocalId--;
      helper.records.push(transitingRecord);
      helper.nextUniqueOrdinal = uniqueOrdinal;
      helper.nextId = newLocalId;
    } else {
      existingEntry.startTime = cloneDeep(transitingSetupChange.timeOfChange);
      existingEntry.startTimeShow = cloneDeep(transitingSetupChange.timeOfChangeToShow);
      existingEntry.sortTime = cloneDeep(transitingSetupChange.timeOfChange.dateTime);
      existingEntry.operationStartTime = cloneDeep(operationInstance.startTime);
      existingEntry.operationName = transitingSetupChange.newValue;

      helper.records.push(existingEntry);
      helper.nextUniqueOrdinal = uniqueOrdinal;
      helper.nextId = newLocalId;
    }

    return helper;
  }
}



// WEBPACK FOOTER //
// ./src/services/daily-log/daily-log-entries/daily-log-entries.service.ts